import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  ChevronsDown,
  ChevronsUp,
  ExternalLink,
  FileText,
  Gavel,
  LibraryBig,
  LoaderCircle,
  Maximize2,
  Minimize2,
  Quote,
  Search,
  ScrollText,
  Sparkles,
  X,
  ZoomIn,
} from "lucide-react";
import {
  AuthenticatedArea,
  AuthPortal,
  SuiteAccountControls,
  useAccessToken,
} from "@tribunalea/auth/react";
import { CitationCard, Justiniano } from "@tribunalea/justiniano";
import {
  useActivityTracking,
} from "@tribunalea/ui";
import {
  ACTIVITY_APPLICATION_IDS,
  ACTIVITY_TOOL_IDS,
} from "@tribunalea/shared";
import { getDocument, GlobalWorkerOptions, TextLayer } from "pdfjs-dist";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createAstreaApi } from "./lib/api.js";

const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const pdfZoomModes = Object.freeze([
  Object.freeze({
    id: "fit-width",
    label: "Ancho",
    description: "Ajustar al ancho",
  }),
  Object.freeze({
    id: "fit-height",
    label: "Alto",
    description: "Ajustar al alto",
  }),
  Object.freeze({
    id: "actual-size",
    label: "100%",
    description: "Tamaño real",
  }),
]);

const panelLayoutStorageKey = "astrea:desktop-panel-layout:v1";
const readerPreferenceStorageKeys = Object.freeze({
  focus: "astrea:reader-focus:v1",
  pageRailExpanded: "astrea:reader-page-rail-expanded:v1",
  zoomMode: "astrea:reader-zoom-mode:v1",
});
const panelResizerWidth = 7;
const panelWidthLimits = Object.freeze({
  catalog: Object.freeze({ min: 210, max: 480 }),
  reader: Object.freeze({ min: 320 }),
  chat: Object.freeze({ min: 350, max: 900 }),
});

function clamp(value, minimum, maximum) {
  return Math.min(Math.max(value, minimum), Math.max(minimum, maximum));
}

function storedPreference(key, fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    const stored = window.localStorage.getItem(key);
    return stored === null ? fallback : JSON.parse(stored);
  } catch {
    return fallback;
  }
}

function persistPreference(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Browser privacy settings can make local storage unavailable.
  }
}

function defaultPanelLayout(viewportWidth = 1440) {
  const catalog = viewportWidth <= 1100
    ? 230
    : viewportWidth <= 1300 ? 250 : 270;
  const chat = clamp(
    Math.round((viewportWidth - catalog - panelResizerWidth * 2) * 0.48),
    panelWidthLimits.chat.min,
    panelWidthLimits.chat.max,
  );
  return { catalog, chat };
}

function normalizedPanelLayout(layout, viewportWidth) {
  const minimumWidth = panelWidthLimits.catalog.min
    + panelWidthLimits.reader.min
    + panelWidthLimits.chat.min
    + panelResizerWidth * 2;
  const availableWidth = Math.max(Number(viewportWidth) || 0, minimumWidth);
  const fallback = defaultPanelLayout(availableWidth);
  const requestedCatalog = Number(layout?.catalog);
  const requestedChat = Number(layout?.chat);
  const catalog = clamp(
    Number.isFinite(requestedCatalog) ? requestedCatalog : fallback.catalog,
    panelWidthLimits.catalog.min,
    Math.min(
      panelWidthLimits.catalog.max,
      availableWidth
        - panelWidthLimits.reader.min
        - panelWidthLimits.chat.min
        - panelResizerWidth * 2,
    ),
  );
  const chat = clamp(
    Number.isFinite(requestedChat) ? requestedChat : fallback.chat,
    panelWidthLimits.chat.min,
    Math.min(
      panelWidthLimits.chat.max,
      availableWidth
        - catalog
        - panelWidthLimits.reader.min
        - panelResizerWidth * 2,
    ),
  );
  return {
    catalog: Math.round(catalog),
    chat: Math.round(chat),
  };
}

function initialPanelLayout() {
  const viewportWidth = typeof window === "undefined" ? 1440 : window.innerWidth;
  if (typeof window === "undefined") return defaultPanelLayout(viewportWidth);
  const layoutWidth = viewportWidth <= 920 ? 1440 : viewportWidth;
  try {
    return normalizedPanelLayout(
      JSON.parse(window.localStorage.getItem(panelLayoutStorageKey)),
      layoutWidth,
    );
  } catch {
    return defaultPanelLayout(layoutWidth);
  }
}

function draggedPanelLayout(layout, panel, delta, viewportWidth) {
  const current = normalizedPanelLayout(layout, viewportWidth);
  if (panel === "catalog") {
    return {
      ...current,
      catalog: Math.round(clamp(
        current.catalog + delta,
        panelWidthLimits.catalog.min,
        Math.min(
          panelWidthLimits.catalog.max,
          viewportWidth
            - current.chat
            - panelWidthLimits.reader.min
            - panelResizerWidth * 2,
        ),
      )),
    };
  }
  return {
    ...current,
    chat: Math.round(clamp(
      current.chat - delta,
      panelWidthLimits.chat.min,
      Math.min(
        panelWidthLimits.chat.max,
        viewportWidth
          - current.catalog
          - panelWidthLimits.reader.min
          - panelResizerWidth * 2,
      ),
    )),
  };
}

function routeSelection() {
  const parameters = new URLSearchParams(window.location.search);
  const page = Number(parameters.get("pagina"));
  return {
    externalBookId: parameters.get("libro"),
    page: Number.isInteger(page) && page > 0 ? page : 1,
  };
}

function normalizedText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es-AR");
}

function bookMatches(book, query) {
  if (!query.trim()) return true;
  const haystack = normalizedText([
    book.title,
    book.subtitle,
    ...(book.authors || []),
    book.publisher,
    book.isbn,
  ].filter(Boolean).join(" "));
  return normalizedText(query)
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => haystack.includes(term));
}

function bookHref(externalBookId, page = 1) {
  const parameters = new URLSearchParams({
    libro: externalBookId,
    pagina: String(page),
  });
  return `/?${parameters}`;
}

function externalBookTarget(href, books) {
  if (!href) return null;
  try {
    const url = new URL(href, window.location.origin);
    if (url.origin === window.location.origin) {
      const externalBookId = url.searchParams.get("libro");
      if (!externalBookId) return null;
      return {
        externalBookId,
        page: Math.max(1, Number(url.searchParams.get("pagina")) || 1),
      };
    }
    if (url.hostname !== "astreavirtual.com") return null;
    const match = url.pathname.match(/^\/reader\/(\d{7,8})\/?$/);
    if (!match) return null;
    const book = books.find(({ externalBookId }) => externalBookId === match[1]);
    const readerPage = Number(url.searchParams.get("page"));
    const physicalPage = Number.isInteger(readerPage)
      ? readerPage - (book?.readerPageOffset || 0)
      : 1;
    return {
      externalBookId: match[1],
      page: Math.max(1, physicalPage),
    };
  } catch {
    return null;
  }
}

function citationBookTarget(citation, books) {
  const metadata = citation.metadata || {};
  const integration = metadata.integration
    || metadata.editionMetadata?.integration
    || metadata.bookMetadata?.integration
    || {};
  const externalBookId = integration.partner === "astrea"
    ? integration.externalBookId
    : null;
  if (externalBookId) {
    return {
      externalBookId,
      page: Math.max(1, Number(metadata.startPage) || 1),
    };
  }
  return externalBookTarget(
    citation.source?.url || metadata.source?.url,
    books,
  );
}

function AstreaCitation({ citation, index, books, onOpenBook }) {
  const target = citationBookTarget(citation, books);
  if (!target) return <CitationCard citation={citation} index={index} />;
  const internalCitation = {
    ...citation,
    source: {
      ...(citation.source || citation.metadata?.source || {}),
      url: null,
    },
  };
  return (
    <div className="astrea-citation">
      <CitationCard
        citation={internalCitation}
        index={index}
        linkMode="tribunalea"
      />
      <a
        href={bookHref(target.externalBookId, target.page)}
        onClick={(event) => {
          event.preventDefault();
          onOpenBook(target.externalBookId, target.page);
        }}
      >
        <BookOpen size={13} /> Leer en Astrea
      </a>
    </div>
  );
}

function BookCover({ book, index, compact = false }) {
  const author = book.authors?.[0] || "Editorial Astrea";
  return (
    <span
      className={`astrea-book-cover tone-${index % 5} ${compact ? "is-compact" : ""}`}
      aria-hidden="true"
    >
      <span>ASTREA</span>
      <strong>{book.title}</strong>
      <small>{author}</small>
    </span>
  );
}

function PdfPage({
  api,
  externalBookId,
  onSelectionChange,
  pageNumber,
  zoomMode,
}) {
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const surfaceRef = useRef(null);
  const textLayerRef = useRef(null);
  const [pdfDocument, setPdfDocument] = useState(null);
  const [status, setStatus] = useState({ loading: true, error: "" });

  useEffect(() => {
    let active = true;
    let loadingTask = null;
    setPdfDocument(null);
    setStatus({ loading: true, error: "" });

    api.getBookPdfSource(externalBookId)
      .then((source) => {
        if (!active) return null;
        loadingTask = getDocument(source);
        return loadingTask.promise;
      })
      .then((document) => {
        if (!document) return;
        if (!active) {
          document.destroy();
          return;
        }
        setPdfDocument(document);
      })
      .catch((error) => {
        if (!active || error?.name === "AbortException") return;
        setStatus({
          loading: false,
          error: error?.message || "No pudimos cargar el PDF original.",
        });
      });

    return () => {
      active = false;
      loadingTask?.destroy();
    };
  }, [api, externalBookId]);

  useEffect(() => {
    if (!pdfDocument || !canvasRef.current || !stageRef.current) return undefined;
    let active = true;
    let frame = null;
    let renderTask = null;
    let textLayer = null;
    setStatus({ loading: true, error: "" });

    async function renderPage() {
      if (!active) return;
      try {
        renderTask?.cancel();
        textLayer?.cancel();
        const page = await pdfDocument.getPage(pageNumber);
        if (!active) {
          page.cleanup();
          return;
        }
        const baseViewport = page.getViewport({ scale: 1 });
        const viewportElement = stageRef.current.parentElement
          || stageRef.current;
        const viewportStyle = window.getComputedStyle(viewportElement);
        const horizontalPadding = (
          Number.parseFloat(viewportStyle.paddingLeft)
          + Number.parseFloat(viewportStyle.paddingRight)
        ) || 0;
        const verticalPadding = (
          Number.parseFloat(viewportStyle.paddingTop)
          + Number.parseFloat(viewportStyle.paddingBottom)
        ) || 0;
        const availableWidth = Math.max(
          120,
          viewportElement.clientWidth - horizontalPadding - 2,
        );
        const availableHeight = Math.max(
          120,
          viewportElement.clientHeight - verticalPadding - 2,
        );
        const displayScale = zoomMode === "actual-size"
          ? 1
          : zoomMode === "fit-height"
            ? availableHeight / baseViewport.height
            : availableWidth / baseViewport.width;
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        const displayViewport = page.getViewport({ scale: displayScale });
        const renderViewport = page.getViewport({
          scale: displayScale * pixelRatio,
        });
        const canvas = canvasRef.current;
        const surface = surfaceRef.current;
        const textLayerHost = textLayerRef.current;
        const textLayerElement = document.createElement("div");
        textLayerElement.className = "astrea-pdf-text-content";
        const context = canvas.getContext("2d", { alpha: false });
        const displayWidth = Math.ceil(displayViewport.width);
        const displayHeight = Math.ceil(displayViewport.height);
        canvas.width = Math.ceil(renderViewport.width);
        canvas.height = Math.ceil(renderViewport.height);
        canvas.style.width = `${displayWidth}px`;
        canvas.style.height = `${displayHeight}px`;
        surface.style.width = `${displayWidth}px`;
        surface.style.height = `${displayHeight}px`;
        surface.style.setProperty("--total-scale-factor", displayScale);
        textLayerHost.replaceChildren(textLayerElement);

        renderTask = page.render({
          canvasContext: context,
          viewport: renderViewport,
        });
        textLayer = new TextLayer({
          container: textLayerElement,
          textContentSource: page.streamTextContent({
            disableNormalization: true,
            includeMarkedContent: true,
          }),
          viewport: displayViewport,
        });
        await Promise.all([renderTask.promise, textLayer.render()]);
        page.cleanup();
        if (active) setStatus({ loading: false, error: "" });
      } catch (error) {
        if (
          !active
          || error?.name === "AbortException"
          || error?.name === "RenderingCancelledException"
        ) {
          return;
        }
        setStatus({
          loading: false,
          error: error?.message || "No pudimos mostrar esta página del PDF.",
        });
      }
    }

    function scheduleRender() {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(renderPage);
    }

    const observer = new ResizeObserver(scheduleRender);
    observer.observe(stageRef.current.parentElement || stageRef.current);
    scheduleRender();

    return () => {
      active = false;
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
      renderTask?.cancel();
      textLayer?.cancel();
    };
  }, [pageNumber, pdfDocument, zoomMode]);

  const captureSelection = useCallback(() => {
    window.setTimeout(() => {
      const selection = window.getSelection();
      const textLayer = textLayerRef.current;
      if (
        !selection
        || selection.isCollapsed
        || !textLayer
        || !textLayer.contains(selection.anchorNode)
        || !textLayer.contains(selection.focusNode)
      ) {
        return;
      }
      const text = selection.toString().replace(/\s+/g, " ").trim();
      if (text) onSelectionChange(text.slice(0, 12_000));
    }, 0);
  }, [onSelectionChange]);

  useEffect(() => {
    document.addEventListener("selectionchange", captureSelection);
    return () => {
      document.removeEventListener("selectionchange", captureSelection);
    };
  }, [captureSelection]);

  return (
    <div
      className="astrea-pdf-page"
      ref={stageRef}
      onKeyUp={captureSelection}
      onMouseUp={captureSelection}
    >
      {status.loading && (
        <div className="astrea-pdf-loading">
          <LoaderCircle className="is-spinning" size={19} />
          Cargando página del PDF…
        </div>
      )}
      {status.error ? (
        <div className="astrea-panel-error">
          <FileText size={22} />
          <strong>No pudimos abrir esta página</strong>
          <p>{status.error}</p>
        </div>
      ) : (
        <div className="astrea-pdf-surface" ref={surfaceRef}>
          <canvas
            className={status.loading ? "is-loading" : ""}
            ref={canvasRef}
            aria-label={`Página ${pageNumber} del PDF original`}
          />
          <div
            className="astrea-pdf-text-layer"
            ref={textLayerRef}
            aria-label={`Texto seleccionable de la página ${pageNumber}`}
          />
        </div>
      )}
    </div>
  );
}

function CatalogPanel({
  books,
  error,
  loading,
  onCloseMobile,
  onOpenBook,
  selectedBook,
}) {
  const [query, setQuery] = useState("");
  const visibleBooks = useMemo(
    () => books.filter((book) => bookMatches(book, query)),
    [books, query],
  );

  return (
    <aside className="astrea-library-panel" id="astrea-catalog-panel">
      <header className="astrea-panel-heading">
        <div>
          <span>CATÁLOGO ASTREA</span>
          <strong>
            {loading ? "Cargando obras" : `${books.length} obra${books.length === 1 ? "" : "s"}`}
          </strong>
        </div>
        <div className="astrea-panel-heading-actions">
          <LibraryBig size={19} aria-hidden="true" />
          <button
            className="astrea-mobile-drawer-close"
            type="button"
            aria-label="Cerrar catálogo"
            onClick={onCloseMobile}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
      </header>

      <label className="astrea-catalog-search">
        <Search size={16} />
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Título, autor o tema"
          aria-label="Buscar en el catálogo de Astrea"
        />
      </label>

      <div className="astrea-book-list">
        {loading ? (
          <div className="astrea-panel-status">
            <LoaderCircle className="is-spinning" size={19} />
            Cargando catálogo…
          </div>
        ) : error ? (
          <div className="astrea-panel-error">
            <LibraryBig size={24} />
            <strong>No pudimos cargar el catálogo</strong>
            <p>{error}</p>
          </div>
        ) : visibleBooks.length ? visibleBooks.map((book, index) => (
          <a
            className={[
              "astrea-book-card",
              selectedBook?.externalBookId === book.externalBookId
                ? "is-selected"
                : "",
            ].filter(Boolean).join(" ")}
            href={bookHref(book.externalBookId)}
            key={book.externalBookId}
            aria-current={
              selectedBook?.externalBookId === book.externalBookId
                ? "page"
                : undefined
            }
            onClick={(event) => {
              event.preventDefault();
              onOpenBook(book.externalBookId, 1);
            }}
          >
            <BookCover book={book} index={index} />
            <span className="astrea-book-card-copy">
              <strong>{book.title}</strong>
              {book.subtitle && <span>{book.subtitle}</span>}
              <small>{book.authors?.join(", ") || "Editorial Astrea"}</small>
              <em>{book.pageCount} páginas</em>
            </span>
            <ChevronRight size={16} />
          </a>
        )) : (
          <div className="astrea-panel-empty">
            No encontramos obras para “{query}”.
          </div>
        )}
      </div>
    </aside>
  );
}

function BookReaderPanel({
  api,
  onClearSelection,
  onPageChange,
  onSelectionChange,
  onToggleFocus,
  pageNumber,
  readerFocus,
  selectedBook,
  selectedText,
}) {
  const pageRailRef = useRef(null);
  const [zoomMode, setZoomMode] = useState(() => {
    const stored = storedPreference(
      readerPreferenceStorageKeys.zoomMode,
      pdfZoomModes[0].id,
    );
    return pdfZoomModes.some(({ id }) => id === stored)
      ? stored
      : pdfZoomModes[0].id;
  });
  const [pageRailExpanded, setPageRailExpanded] = useState(() => {
    const stored = storedPreference(
      readerPreferenceStorageKeys.pageRailExpanded,
      true,
    );
    return typeof stored === "boolean" ? stored : true;
  });
  const pageCount = selectedBook?.pageCount || 0;
  const pages = useMemo(
    () => Array.from({ length: pageCount }, (_, index) => index + 1),
    [pageCount],
  );

  useEffect(() => {
    persistPreference(readerPreferenceStorageKeys.zoomMode, zoomMode);
  }, [zoomMode]);

  useEffect(() => {
    persistPreference(
      readerPreferenceStorageKeys.pageRailExpanded,
      pageRailExpanded,
    );
  }, [pageRailExpanded]);

  useEffect(() => {
    const currentPage = pageRailRef.current?.querySelector(
      `[data-page="${pageNumber}"]`,
    );
    currentPage?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [pageCount, pageNumber, pageRailExpanded]);

  if (!selectedBook) {
    return (
      <section className="astrea-reader-panel is-empty">
        <header className="astrea-panel-heading">
          <div>
            <span>LECTOR ASTREA</span>
            <strong>Contenido del libro</strong>
          </div>
          <BookOpen size={19} aria-hidden="true" />
        </header>
        <div className="astrea-reader-empty">
          <BookOpen size={30} />
          <h2>Elegí una obra del catálogo</h2>
          <p>El PDF original se abrirá acá, una página a la vez.</p>
        </div>
      </section>
    );
  }

  const zoomModeIndex = pdfZoomModes.findIndex(({ id }) => id === zoomMode);
  const activeZoomMode = pdfZoomModes[zoomModeIndex] || pdfZoomModes[0];
  const nextZoomMode = pdfZoomModes[
    (Math.max(0, zoomModeIndex) + 1) % pdfZoomModes.length
  ];
  const panelClassName = [
    "astrea-reader-panel",
    selectedText ? "has-selection" : "",
    pageRailExpanded ? "" : "is-pagination-collapsed",
  ].filter(Boolean).join(" ");
  return (
    <section className={panelClassName}>
      <header className="astrea-reader-toolbar">
        <div className="astrea-reader-context">
          <span>LECTOR ASTREA</span>
          <div className="astrea-reader-bookline">
            <h2 title={selectedBook.title}>{selectedBook.title}</h2>
            <i aria-hidden="true">·</i>
            <p title={selectedBook.authors?.join(" · ") || "Editorial Astrea"}>
              {selectedBook.authors?.join(" · ") || "Editorial Astrea"}
            </p>
          </div>
        </div>
        <div className="astrea-reader-actions">
          <output
            className="astrea-page-position"
            aria-label={`Página ${pageNumber} de ${pageCount}`}
            title={`Página ${pageNumber} de ${pageCount}`}
          >
            <strong>{pageNumber}</strong><span>/</span>{pageCount}
          </output>
          <button
            type="button"
            aria-label={`Zoom: ${activeZoomMode.description}. Cambiar a ${nextZoomMode.description.toLocaleLowerCase("es-AR")}`}
            title={`${activeZoomMode.description} · Siguiente: ${nextZoomMode.label}`}
            onClick={() => setZoomMode(nextZoomMode.id)}
          >
            <ZoomIn size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-expanded={pageRailExpanded}
            aria-label={pageRailExpanded ? "Ocultar selector de páginas" : "Mostrar selector de páginas"}
            title={pageRailExpanded ? "Ocultar páginas" : "Mostrar páginas"}
            onClick={() => setPageRailExpanded((expanded) => !expanded)}
          >
            {pageRailExpanded
              ? <ChevronsUp size={16} aria-hidden="true" />
              : <ChevronsDown size={16} aria-hidden="true" />}
          </button>
          <button
            className="astrea-reader-focus-toggle"
            type="button"
            aria-pressed={readerFocus}
            aria-label={readerFocus ? "Salir del modo lectura" : "Activar modo lectura"}
            title={readerFocus ? "Salir del modo lectura" : "Modo lectura"}
            onClick={onToggleFocus}
          >
            {readerFocus
              ? <Minimize2 size={16} aria-hidden="true" />
              : <Maximize2 size={16} aria-hidden="true" />}
          </button>
        </div>
      </header>

      {pageRailExpanded && <div className="astrea-page-controls" aria-label="Navegación del libro">
        <button
          className="astrea-page-step"
          type="button"
          aria-label="Página anterior"
          disabled={pageNumber <= 1}
          onClick={() => onPageChange(pageNumber - 1)}
        >
          <ChevronLeft size={17} />
        </button>
        <div
          className="astrea-page-rail"
          ref={pageRailRef}
          role="navigation"
          aria-label={`Páginas del libro, ${pageNumber} de ${pageCount}`}
        >
          {pages.map((page) => (
            <button
              className={`astrea-page-preview${page === pageNumber ? " is-current" : ""}`}
              type="button"
              aria-label={`Ir a la página ${page}`}
              aria-current={page === pageNumber ? "page" : undefined}
              data-page={page}
              key={page}
              onClick={() => onPageChange(page)}
            >
              <span>{page}</span>
            </button>
          ))}
        </div>
        <button
          className="astrea-page-step"
          type="button"
          aria-label="Página siguiente"
          disabled={pageNumber >= (selectedBook.pageCount || pageNumber)}
          onClick={() => onPageChange(pageNumber + 1)}
        >
          <ChevronRight size={17} />
        </button>
      </div>}

      {selectedText && (
        <div className="astrea-selection-banner" role="status">
          <Quote size={15} aria-hidden="true" />
          <span>
            <strong>Selección actual</strong>
            <small>{selectedText}</small>
          </span>
          <button
            type="button"
            aria-label="Quitar selección actual"
            onClick={onClearSelection}
          >
            <X size={14} />
          </button>
        </div>
      )}

      <div className="astrea-page-stage">
        <PdfPage
          api={api}
          externalBookId={selectedBook.externalBookId}
          onSelectionChange={onSelectionChange}
          pageNumber={pageNumber}
          zoomMode={zoomMode}
        />
      </div>
    </section>
  );
}

function AstreaWorkspace({ authEnabled, getAccessToken }) {
  const workspaceRef = useRef(null);
  const resizeCleanupRef = useRef(null);
  const api = useMemo(
    () => createAstreaApi(getAccessToken, apiUrl),
    [getAccessToken],
  );
  const [books, setBooks] = useState([]);
  const [catalogState, setCatalogState] = useState({
    loading: true,
    error: "",
  });
  const [selectedBook, setSelectedBook] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [readerSelection, setReaderSelection] = useState(null);
  const [readerFocus, setReaderFocus] = useState(() => {
    const stored = storedPreference(readerPreferenceStorageKeys.focus, false);
    return typeof stored === "boolean" ? stored : false;
  });
  const [mobilePanel, setMobilePanel] = useState(null);
  const [panelLayout, setPanelLayout] = useState(initialPanelLayout);

  const updateLocation = useCallback((book, page) => {
    const href = book ? bookHref(book.externalBookId, page) : "/";
    window.history.replaceState({}, "", href);
  }, []);

  const openBook = useCallback((externalBookId, requestedPage = 1) => {
    const book = books.find((item) => item.externalBookId === externalBookId);
    if (!book) return;
    const page = Math.max(
      1,
      Math.min(Number(requestedPage) || 1, book.pageCount || 1),
    );
    setSelectedBook(book);
    setPageNumber(page);
    setReaderSelection(null);
    setMobilePanel(null);
    updateLocation(book, page);
  }, [books, updateLocation]);

  const changePage = useCallback((requestedPage) => {
    if (!selectedBook) return;
    const page = Math.max(
      1,
      Math.min(Number(requestedPage) || 1, selectedBook.pageCount || 1),
    );
    setPageNumber(page);
    setReaderSelection(null);
    updateLocation(selectedBook, page);
  }, [selectedBook, updateLocation]);

  const closeBook = useCallback(() => {
    setSelectedBook(null);
    setReaderSelection(null);
    setMobilePanel(null);
    window.history.replaceState({}, "", "/");
  }, []);

  useEffect(() => {
    persistPreference(readerPreferenceStorageKeys.focus, readerFocus);
  }, [readerFocus]);

  useEffect(() => {
    if (!mobilePanel) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMobilePanel(null);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [mobilePanel]);

  useEffect(() => {
    const persistLayout = window.setTimeout(() => {
      try {
        window.localStorage.setItem(
          panelLayoutStorageKey,
          JSON.stringify(panelLayout),
        );
      } catch {
        // Browser privacy settings can make local storage unavailable.
      }
    }, 120);
    return () => window.clearTimeout(persistLayout);
  }, [panelLayout]);

  useEffect(() => {
    const constrainLayoutToViewport = () => {
      if (window.matchMedia("(max-width: 920px)").matches) return;
      const width = workspaceRef.current?.getBoundingClientRect().width
        || window.innerWidth;
      setPanelLayout((current) => {
        const next = normalizedPanelLayout(current, width);
        return next.catalog === current.catalog && next.chat === current.chat
          ? current
          : next;
      });
    };
    window.addEventListener("resize", constrainLayoutToViewport);
    constrainLayoutToViewport();
    return () => window.removeEventListener("resize", constrainLayoutToViewport);
  }, []);

  useEffect(() => () => resizeCleanupRef.current?.(), []);

  const resetPanelLayout = useCallback(() => {
    const width = workspaceRef.current?.getBoundingClientRect().width
      || window.innerWidth;
    setPanelLayout(defaultPanelLayout(width));
  }, []);

  const startPanelResize = useCallback((panel, event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (window.matchMedia("(max-width: 920px)").matches) return;
    event.preventDefault();
    resizeCleanupRef.current?.();
    const handle = event.currentTarget;
    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startLayout = panelLayout;
    const viewportWidth = workspaceRef.current?.getBoundingClientRect().width
      || window.innerWidth;
    handle.setPointerCapture?.(pointerId);
    document.body.classList.add("is-resizing-astrea-panels");

    const resize = (moveEvent) => {
      setPanelLayout(draggedPanelLayout(
        startLayout,
        panel,
        moveEvent.clientX - startX,
        viewportWidth,
      ));
    };
    const finish = () => {
      window.removeEventListener("pointermove", resize);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
      window.removeEventListener("blur", finish);
      document.body.classList.remove("is-resizing-astrea-panels");
      try {
        handle.releasePointerCapture?.(pointerId);
      } catch {
        // Pointer capture may already be released by the browser.
      }
      resizeCleanupRef.current = null;
    };
    resizeCleanupRef.current = finish;
    window.addEventListener("pointermove", resize);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", finish);
    window.addEventListener("blur", finish);
  }, [panelLayout]);

  const resizePanelWithKeyboard = useCallback((panel, event) => {
    if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
    event.preventDefault();
    const direction = event.key === "ArrowLeft" ? -1 : 1;
    const delta = direction * (event.shiftKey ? 48 : 16);
    const viewportWidth = workspaceRef.current?.getBoundingClientRect().width
      || window.innerWidth;
    setPanelLayout((current) => draggedPanelLayout(
      current,
      panel,
      delta,
      viewportWidth,
    ));
  }, []);

  const captureReaderSelection = useCallback((text) => {
    if (!selectedBook || !text) return;
    setReaderSelection({
      externalBookId: selectedBook.externalBookId,
      pageNumber,
      text,
    });
  }, [pageNumber, selectedBook]);

  const selectedText = readerSelection
    && readerSelection.externalBookId === selectedBook?.externalBookId
    && readerSelection.pageNumber === pageNumber
    ? readerSelection.text
    : "";

  useEffect(() => {
    let active = true;
    api.listBooks()
      .then((items) => {
        if (!active) return;
        setBooks(items);
        const route = routeSelection();
        const requested = items.find(({ externalBookId }) => (
          externalBookId === route.externalBookId
        ));
        if (requested) {
          const page = Math.max(1, Math.min(route.page, requested.pageCount || 1));
          setSelectedBook(requested);
          setPageNumber(page);
        } else if (window.matchMedia("(max-width: 920px)").matches) {
          setMobilePanel("catalog");
        }
        setCatalogState({ loading: false, error: "" });
      })
      .catch((error) => {
        if (!active) return;
        setCatalogState({
          loading: false,
          error: error.message || "No pudimos cargar el catálogo.",
        });
      });
    return () => {
      active = false;
    };
  }, [api]);

  const renderers = useMemo(() => ({
    renderCitation: ({ citation, index }) => {
      const target = citationBookTarget(citation, books);
      if (!target) return null;
      return (
        <AstreaCitation
          citation={citation}
          index={index}
          books={books}
          onOpenBook={openBook}
        />
      );
    },
    renderLink: ({ children, href, props }) => {
      const target = externalBookTarget(href, books);
      if (!target) return null;
      return (
        <a
          {...props}
          href={bookHref(target.externalBookId, target.page)}
          onClick={(event) => {
            event.preventDefault();
            openBook(target.externalBookId, target.page);
          }}
        >
          {children}
        </a>
      );
    },
  }), [books, openBook]);

  const appContext = useMemo(() => ({
    route: selectedBook ? bookHref(selectedBook.externalBookId, pageNumber) : "/",
    interfaceHelp: false,
    activeEntity: selectedBook ? {
      type: "partner_book",
      id: selectedBook.externalBookId,
      label: selectedBook.title,
    } : null,
    pageState: {
      documentOpen: Boolean(selectedBook),
      documentType: "partner_book",
      partnerId: "astrea",
      externalBookId: selectedBook?.externalBookId || null,
      fileName: selectedBook?.title || null,
      pageLabel: selectedBook ? String(pageNumber) : null,
      displayedPage: selectedBook ? pageNumber : null,
      viewerPage: selectedBook ? pageNumber : null,
      viewerPageIndex: selectedBook ? pageNumber - 1 : null,
      physicalPage: selectedBook ? pageNumber : null,
      totalPages: selectedBook?.pageCount || null,
      selectedText: selectedText || null,
      selectionPage: selectedText ? pageNumber : null,
    },
  }), [pageNumber, selectedBook, selectedText]);

  return (
    <div className="astrea-app">
      <header className="astrea-topbar">
        <a className="astrea-brand" href="/" onClick={(event) => {
          event.preventDefault();
          closeBook();
        }}>
          <span className="astrea-brand-mark">A</span>
          <span>
            <strong>ASTREA</strong>
            <small>Biblioteca jurídica inteligente</small>
          </span>
        </a>

        <div className="astrea-source-strip" aria-label="Fuentes disponibles">
          <span><BookOpen size={14} /> Doctrina Astrea</span>
          <span><Gavel size={14} /> Jurisprudencia</span>
          <span><ScrollText size={14} /> Normativa</span>
        </div>

        <div className="astrea-account-area">
          <a
            href="https://tribunalea.com"
            target="_blank"
            rel="noreferrer"
            className="astrea-powered"
          >
            <Sparkles size={13} /> Powered by Tribunalea
            <ExternalLink size={11} />
          </a>
          {authEnabled && <SuiteAccountControls />}
        </div>
      </header>

      <main className={[
        "astrea-workspace",
        selectedBook && readerFocus ? "is-reader-focus" : "",
        mobilePanel === "catalog" ? "is-mobile-catalog-open" : "",
        mobilePanel === "chat" ? "is-mobile-chat-open" : "",
      ].filter(Boolean).join(" ")}
      ref={workspaceRef}
      style={{
        "--astrea-catalog-width": `${panelLayout.catalog}px`,
        "--astrea-chat-width": `${panelLayout.chat}px`,
      }}>
        {mobilePanel && (
          <button
            className="astrea-mobile-scrim"
            type="button"
            aria-label="Cerrar panel"
            onClick={() => setMobilePanel(null)}
          />
        )}
        <CatalogPanel
          books={books}
          error={catalogState.error}
          loading={catalogState.loading}
          onCloseMobile={() => setMobilePanel(null)}
          onOpenBook={openBook}
          selectedBook={selectedBook}
        />

        <div
          className="astrea-panel-resizer"
          role="separator"
          tabIndex={0}
          aria-label="Redimensionar catálogo"
          aria-orientation="vertical"
          aria-valuemin={panelWidthLimits.catalog.min}
          aria-valuemax={panelWidthLimits.catalog.max}
          aria-valuenow={panelLayout.catalog}
          aria-valuetext={`Catálogo: ${panelLayout.catalog} píxeles`}
          title="Arrastrá para redimensionar · Doble clic para restablecer"
          onDoubleClick={resetPanelLayout}
          onKeyDown={(event) => resizePanelWithKeyboard("catalog", event)}
          onPointerDown={(event) => startPanelResize("catalog", event)}
        />

        <BookReaderPanel
          api={api}
          onClearSelection={() => setReaderSelection(null)}
          onPageChange={changePage}
          onSelectionChange={captureReaderSelection}
          onToggleFocus={() => setReaderFocus((focused) => !focused)}
          pageNumber={pageNumber}
          readerFocus={readerFocus}
          selectedBook={selectedBook}
          selectedText={selectedText}
        />

        <div
          className="astrea-panel-resizer"
          role="separator"
          tabIndex={0}
          aria-label="Redimensionar lector y Justiniano"
          aria-orientation="vertical"
          aria-valuemin={panelWidthLimits.chat.min}
          aria-valuemax={panelWidthLimits.chat.max}
          aria-valuenow={panelLayout.chat}
          aria-valuetext={`Justiniano: ${panelLayout.chat} píxeles`}
          title="Arrastrá para redimensionar · Doble clic para restablecer"
          onDoubleClick={resetPanelLayout}
          onKeyDown={(event) => resizePanelWithKeyboard("chat", event)}
          onPointerDown={(event) => startPanelResize("chat", event)}
        />

        <section
          className="astrea-intelligence-workspace"
          id="astrea-chat-panel"
        >
          <button
            className="astrea-mobile-chat-close"
            type="button"
            aria-label="Cerrar Justiniano"
            onClick={() => setMobilePanel(null)}
          >
            <X size={18} aria-hidden="true" />
          </button>
          <Justiniano
            variant="page"
            appId="astrea"
            appName="Astrea"
            assistantName="Justiniano"
            apiUrl={apiUrl}
            getAccessToken={getAccessToken}
            renderers={renderers}
            poweredBy={{
              label: "Powered by Tribunalea",
              url: "https://tribunalea.com",
            }}
            welcome={selectedBook
              ? `Estoy listo para trabajar con “${selectedBook.title}”, buscar en el resto del catálogo de Astrea o conectarlo con jurisprudencia y normativa.`
              : "Elegí una obra del catálogo o preguntame por un tema. Puedo buscar en los libros de Astrea, jurisprudencia y normativa, y convertir el resultado en una redacción."}
            suggestions={[
              "Buscá en Astrea obras sobre responsabilidad civil",
              "¿Qué jurisprudencia reciente se relaciona con este tema?",
              "Prepará una redacción a partir de nuestra investigación",
            ]}
            appContext={appContext}
            signatureSettingsHref={`${import.meta.env.VITE_ACCOUNT_URL || "https://account.tribunalea.com"}/firma`}
          />
        </section>
      </main>

      <nav className="astrea-mobile-nav" aria-label="Paneles de Astrea">
        <button
          type="button"
          className={mobilePanel === "catalog" ? "is-active" : ""}
          aria-controls="astrea-catalog-panel"
          aria-expanded={mobilePanel === "catalog"}
          onClick={() => setMobilePanel((panel) => (
            panel === "catalog" ? null : "catalog"
          ))}
        >
          <LibraryBig size={19} aria-hidden="true" />
          <span>Catálogo</span>
        </button>
        <button
          type="button"
          className={mobilePanel === "chat" ? "is-active" : ""}
          aria-controls="astrea-chat-panel"
          aria-expanded={mobilePanel === "chat"}
          onClick={() => setMobilePanel((panel) => (
            panel === "chat" ? null : "chat"
          ))}
        >
          <Sparkles size={19} aria-hidden="true" />
          <span>Justiniano</span>
        </button>
      </nav>
    </div>
  );
}

function ClerkWorkspace() {
  const getAccessToken = useAccessToken();
  useActivityTracking({
    apiUrl,
    getAccessToken,
    appId: ACTIVITY_APPLICATION_IDS.ASTREA,
    toolId: ACTIVITY_TOOL_IDS.ASTREA_LIBRARY,
  });
  return <AstreaWorkspace authEnabled getAccessToken={getAccessToken} />;
}

function SignInScreen() {
  return (
    <AuthPortal
      productName="ASTREA"
      headline="Todo el conocimiento jurídico, listo para trabajar."
      description="Explorá las obras de Astrea, investigá con Justiniano y convertí tus hallazgos en una redacción sin cambiar de espacio."
      proofPoints={[
        "Catálogo Astrea con lectura por página",
        "Investigación jurídica con fuentes",
        "Redacción asistida en el mismo espacio",
      ]}
    />
  );
}

export default function App({ authEnabled }) {
  if (authEnabled) {
    return (
      <AuthenticatedArea signedOut={<SignInScreen />}>
        <ClerkWorkspace />
      </AuthenticatedArea>
    );
  }
  return <AstreaWorkspace authEnabled={false} getAccessToken={null} />;
}
